import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

// Utils
import { useAppState } from '../context/AppContext'
import { isAdmin } from '../common/helpers/isLeadOf'
import {
  bundleActivityItems,
  IActivityItem,
  notificationToActivityItem,
  requestToActivityItem,
} from '../utils/activityFeed'
import {
  filterRequestsByModuleAccess,
  getCompanyRequests,
  getOwnRequests,
} from '../utils/requestsHelper'

// Types
import { TimeGroup } from '../utils/dateHelper'

export interface IActivityGroup {
  group: TimeGroup
  items: IActivityItem[]
}

interface IActivityFeed {
  /** Open requests, newest first. Never bundled: each is its own task. */
  requests: IActivityItem[]
  /** Recently finished requests, newest completion first. Hidden behind a toggle. */
  completedRequests: IActivityItem[]
  /** Notifications, bundled and split into Today / This week / Earlier. */
  activity: IActivityGroup[]
  openRequestCount: number
  /** Whether any activity row is still unread — a dot's worth of news, not a count. */
  hasUnreadActivity: boolean
}

const GROUP_ORDER: TimeGroup[] = ['today', 'week', 'earlier']

/**
 * App state holds every request a member ever had, across all terms, so the finished
 * list is unbounded. The panel is a to-do list with a short memory, not an archive —
 * and it is no full history either: requests made irrelevant by someone else (revoked
 * feedback, a revoked evaluation) are deleted rather than completed.
 */
const COMPLETED_REQUEST_LIMIT = 20

const byNewestFirst = (a: IActivityItem, b: IActivityItem) =>
  b.date.getTime() - a.date.getTime()

/**
 * The two feeds behind the activity panel. Both sources already live in app state,
 * so this merges nothing server-side: it maps each onto the shared IActivityItem
 * shape, bundles repeat notifications, and groups them by age.
 */
const useActivityFeed = (): IActivityFeed => {
  const { t } = useTranslation()
  const { notifications, requests, company, user } = useAppState()

  const members = company?.allMembers || []
  const terms = company?.termsInformation?.terms || []
  const authMember = user?.asCompanyMember

  const { openRequests, completedRequests } = useMemo(() => {
    const available = filterRequestsByModuleAccess(requests, company)
    // An admin owns the company-level requests on top of their personal ones.
    const visible = [
      ...getOwnRequests(available),
      ...(authMember && isAdmin(authMember)
        ? getCompanyRequests(available)
        : []),
    ]

    const items = visible
      .map((request) => requestToActivityItem(request, members, terms, t))
      .sort(byNewestFirst)

    return {
      openRequests: items.filter((item) => !item.isCompleted),
      completedRequests: items
        .filter((item) => item.isCompleted)
        .slice(0, COMPLETED_REQUEST_LIMIT),
    }
  }, [requests, company, authMember, members, terms, t])

  const activity = useMemo(() => {
    const items = bundleActivityItems(
      notifications
        .map((notification) =>
          notificationToActivityItem(notification, members, t)
        )
        .filter((item): item is IActivityItem => !!item)
        .sort(byNewestFirst)
    )

    return GROUP_ORDER.map((group) => ({
      group,
      items: items.filter((item) => item.timeGroup === group),
    })).filter((section) => section.items.length > 0)
  }, [notifications, members, t])

  return {
    requests: openRequests,
    completedRequests,
    activity,
    // The bell badge stays a count of work waiting: finished rows never add to it.
    openRequestCount: openRequests.length,
    // Read off the built groups, so hidden and unknown types cannot raise a dot
    // over a row the panel does not render.
    hasUnreadActivity: activity.some((section) =>
      section.items.some((item) => item.isUnread)
    ),
  }
}

export default useActivityFeed
